import { useState, useEffect } from 'react';
import { NotificationPrompt, usePushNotifications } from './PushNotifications';

/**
 * PushNotificationManager
 * Manages the lifecycle of Web Push subscription for authenticated users.
 * - Auto-subscribes if permission was already granted.
 * - Shows a non-intrusive prompt once per week for users who haven't decided yet.
 * - Suppresses prompt for 7 days after dismissal.
 */
export default function PushNotificationManager() {
  const [showPrompt, setShowPrompt] = useState(false);
  const { isSupported, isSubscribed, subscribe, permission } = usePushNotifications();

  useEffect(() => {
    // Only show for authenticated users
    const token = localStorage.getItem('token');
    if (!token || !isSupported || isSubscribed) return;

    // If already granted, auto-subscribe silently
    if (permission === 'granted') {
      subscribe();
      return;
    }

    // Don't prompt again if user previously denied in browser
    if (permission === 'denied') return;

    // Respect the 7-day cooldown after dismiss
    const lastPrompted = localStorage.getItem('push_prompted_at');
    if (lastPrompted) {
      const daysSince = (Date.now() - parseInt(lastPrompted, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // Show prompt after 4 seconds so it doesn't interrupt page load
    const timer = setTimeout(() => setShowPrompt(true), 4000);
    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, permission, subscribe]);

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('push_prompted_at', Date.now().toString());
  };

  if (!showPrompt) return null;

  return <NotificationPrompt onClose={handleClose} />;
}
